import React, { useEffect, useRef } from 'react';
import { useChatContext } from '../context/ChatContext';
import { TRANSLATIONS, FEATURE_ICONS } from '../constants';
import { Message } from '../types';
import { getHealthResponse } from '../services/geminiService';
import { ChatInput } from '../components/ChatInput';
import { ChatMessage } from '../components/ChatMessage';
import { HealthDisclaimer } from '../components/HealthDisclaimer';
import { ArrowLeft, RefreshCw, Download, Activity } from 'lucide-react';

export const ChatPage: React.FC = () => {
  const { state, dispatch } = useChatContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const t = TRANSLATIONS[state.selectedLanguage];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, state.isLoading]);

  const handleSend = async (text: string) => {
    if (!text.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
      language: state.selectedLanguage,
    };
    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const { response, triageLevel } = await getHealthResponse(
        text,
        state.selectedLanguage,
        state.messages.map((m) => ({ role: m.role, content: m.content }))
      );

      const modelMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'model',
        content: response,
        timestamp: Date.now(),
        language: state.selectedLanguage,
        triageLevel,
      };
      dispatch({ type: 'ADD_MESSAGE', payload: modelMessage });
    } catch (err) {
      console.error(err);
      dispatch({ type: 'SET_ERROR', payload: t.error });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleSaveChat = () => {
    if (state.messages.length === 0) return;
    const timestamp = new Date().toLocaleString();
    const header = `SisterAI Consultation Record\nDate: ${timestamp}\nLanguage: ${state.selectedLanguage}\n\nIMPORTANT: ${t.disclaimerText}\n\n${'─'.repeat(60)}\n\n`;
    const body = state.messages
      .map(
        (m) =>
          `[${new Date(m.timestamp).toLocaleTimeString()}] ${m.role === 'user' ? 'Patient' : 'SisterAI'}${m.triageLevel ? ` [${m.triageLevel.toUpperCase()}]` : ''}:\n${m.content}`
      )
      .join(`\n\n${'─'.repeat(60)}\n\n`);

    const blob = new Blob([header + body], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sisterai-consultation-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-screen bg-neutral-950">
      {/* Header */}
      <header className="flex-shrink-0 bg-neutral-950 border-b border-white/10 sticky top-0 z-10 backdrop-blur-md bg-opacity-80">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => dispatch({ type: 'RESET_SESSION' })}
              className="p-2 hover:bg-white/5 rounded-full transition-colors text-gray-400 hover:text-white"
              aria-label="Back to Home"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-brand-400" />
              <h1 className="text-lg font-bold text-white tracking-wide font-sans hidden md:block">
                Sister<span className="text-brand-400">AI</span>
              </h1>
            </div>
            <div className="h-4 w-px bg-white/10 mx-1" />
            <span className="text-xs text-brand-300 font-medium border border-brand-500/30 bg-brand-900/20 px-2 py-0.5 rounded">
              {state.selectedLanguage.toUpperCase()}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveChat}
              disabled={state.messages.length === 0}
              className="text-sm font-medium text-gray-400 hover:text-brand-400 flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title={t.saveChat}
            >
              <Download size={16} />
              <span className="hidden sm:inline">{t.saveChat}</span>
            </button>
            <button
              onClick={() => dispatch({ type: 'CLEAR_CHAT' })}
              className="text-sm font-medium text-gray-400 hover:text-brand-400 flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors"
              title={t.newChat}
            >
              <RefreshCw size={16} />
              <span className="hidden sm:inline">{t.newChat}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main chat */}
      <main className="flex-1 overflow-y-auto bg-neutral-950 scroll-smooth">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <HealthDisclaimer title={t.disclaimerTitle} text={t.disclaimerText} />

          {/* Empty state */}
          {state.messages.length === 0 && (
            <div className="text-center py-10 opacity-80">
              <h3 className="text-2xl font-serif italic text-white mb-8">{t.features.title}</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {t.features.items.map((item, i) => {
                  const Icon = FEATURE_ICONS[i] || FEATURE_ICONS[0];
                  return (
                    <button
                      key={i}
                      onClick={() => handleSend(item)}
                      className="flex items-center gap-2 px-5 py-2.5 bg-neutral-900 border border-white/10 hover:border-brand-500/50 hover:bg-neutral-800 rounded-lg text-sm text-gray-300 shadow-sm transition-all duration-200"
                    >
                      <Icon className="w-4 h-4 text-brand-400 flex-shrink-0" />
                      <span>{item}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex flex-col">
            {state.messages.map((msg) => (
              <ChatMessage key={msg.id} message={msg} translations={t} />
            ))}

            {/* Loading */}
            {state.isLoading && (
              <div className="flex justify-start mb-6">
                <div className="bg-neutral-900 border border-white/10 px-5 py-4 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-2">
                  <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  <span className="text-xs text-gray-500 ml-2">{t.loading}</span>
                </div>
              </div>
            )}

            {/* Error */}
            {state.error && (
              <div className="p-4 mb-6 text-red-400 bg-red-900/10 rounded-lg border border-red-900/30 text-center text-sm">
                {state.error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </main>

      {/* Input */}
      <div className="flex-shrink-0 bg-neutral-950 border-t border-white/10 p-4 pb-6">
        <ChatInput onSend={handleSend} isLoading={state.isLoading} translations={t} />
        <div className="text-center mt-3">
          <span className="text-[10px] text-gray-600 uppercase tracking-widest">{t.disclaimerFooter}</span>
        </div>
      </div>
    </div>
  );
};
